import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Eye, Bug, Rocket } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchApps, searchAppsByName } from '../store/appsSlice'
import StatusFilter from '../components/StatusFilter'
import NameFilter from '../components/NameFilter'
import RequestAppCTA from '../components/RequestAppCTA'
import AppCardGrid from '../components/AppCardGrid'
import './MainPage.css'

function MainPage() {
  const dispatch = useAppDispatch()
  const loading = useAppSelector((state) => state.apps.loading)
  const error = useAppSelector((state) => state.apps.error)
  const activeFilter = useAppSelector((state) => state.apps.activeFilter)
  const nameFilter = useAppSelector((state) => state.apps.nameFilter)
  const offset = useAppSelector((state) => state.apps.offset)
  const pageSize = useAppSelector((state) => state.apps.pageSize)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!nameFilter) {
      dispatch(fetchApps({ status: activeFilter, offset, limit: pageSize }))
    }
  }, [dispatch, activeFilter, offset, pageSize, nameFilter])

  useEffect(() => {
    if (!nameFilter) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      dispatch(searchAppsByName({ query: nameFilter, status: activeFilter }))
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [dispatch, nameFilter, activeFilter, pageSize])

  return (
    <div className="main-page" data-testid="main-page">
      <section className="hero">
        <h1 className="hero__title">
          You Describe it. <span className="hero__accent">We build it.</span>
        </h1>
        <p className="hero__subtitle">
          Request a web app in plain English. Loop Builder autonomously builds, tests, deploys, and
          maintains it for you — no coding required.
        </p>
        <p className="hero__notice">
          All Loop Builder apps and conversations are public.{' '}
          {/* Test: HeroSourceLink: View source code link visible in hero */}
          <a href="https://github.com/replayio/app-building" target="_blank" rel="noopener noreferrer">
            View source code
          </a>{' '}
          for self-hosting.
        </p>
        <div className="hero__steps">
          <div className="hero__step">
            <div className="hero__step-icon" aria-hidden="true"><Sparkles size={20} /></div>
            <div>
              <h2 className="hero__step-title">Request</h2>
              <p className="hero__step-text">Describe what you need and submit</p>
            </div>
          </div>
          <div className="hero__step">
            <div className="hero__step-icon" aria-hidden="true"><Eye size={20} /></div>
            <div>
              <h2 className="hero__step-title">Watch</h2>
              <p className="hero__step-text">Monitor the live build &amp; deploy</p>
            </div>
          </div>
          <div className="hero__step">
            <div className="hero__step-icon" aria-hidden="true"><Bug size={20} /></div>
            <div>
              <h2 className="hero__step-title">Iterate</h2>
              <p className="hero__step-text">Report bugs and they get auto-fixed</p>
            </div>
          </div>
          <div className="hero__step">
            <div className="hero__step-icon" aria-hidden="true"><Rocket size={20} /></div>
            <div>
              <h2 className="hero__step-title">Ship</h2>
              <p className="hero__step-text">Open your live app or grab the source</p>
            </div>
          </div>
        </div>
      </section>

      <section className="powered-by">
        <div className="powered-by__content">
          <img src="/images/logo.svg" alt="" className="powered-by__logo" width="28" height="32" />
          <div>
            <h2 className="powered-by__title">
              Built on <span className="powered-by__accent">Replay</span>
            </h2>
            <p className="powered-by__text">
              Loop Builder uses Replay's MCP server to give AI agents the ability to inspect runtime
              behavior, diagnose failures, and fix issues autonomously — turning time-travel debugging
              into a superpower for agentic development.
            </p>
          </div>
        </div>
        {/* Test: PoweredByLink: Learn more at Replay.io link visible */}
        <a
          href="https://replay.io"
          target="_blank"
          rel="noopener noreferrer"
          className="powered-by__cta"
        >
          Learn more at Replay.io &rarr;
        </a>
      </section>

      <div className="main-page__sticky-controls">
        <StatusFilter />
        <NameFilter />
        <RequestAppCTA />
      </div>
      <div className="main-page__apps" aria-live="polite">
        {loading ? (
          <div className="skeleton-grid" data-testid="main-page-loading" role="status" aria-label="Loading apps">
            {[0, 1, 2].map((i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton-card__top">
                  <div className="skeleton-line skeleton-line--title" />
                  <div className="skeleton-line skeleton-line--text" />
                  <div className="skeleton-line skeleton-line--text skeleton-line--short" />
                </div>
                <div className="skeleton-card__bottom">
                  <div className="skeleton-line skeleton-line--meta" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="main-page__error" data-testid="main-page-error" role="alert">
            <span className="main-page__error-icon" aria-hidden="true">!</span>
            <p className="main-page__error-text">{error}</p>
          </div>
        ) : (
          <AppCardGrid />
        )}
      </div>

      <footer className="main-page__footer">
        <nav className="main-page__footer-links" aria-label="Footer">
          {/* Test: FooterLinks: Replay link visible in footer */}
          <a href="https://replay.io" target="_blank" rel="noopener noreferrer" className="main-page__footer-link">
            <img src="/images/logo.svg" alt="Replay" className="main-page__footer-logo" width="16" height="16" />
            Replay
          </a>
          <span className="main-page__footer-separator">·</span>
          <p className="main-page__copyright">
            Copyright {new Date().getFullYear()} &copy;{' '}
              Replay
          </p>
          <span className="main-page__footer-separator">·</span>
          {/* Test: FooterLinks: About link visible in footer */}
          <a href="https://blog.replay.io/open-auto-builder" target="_blank" rel="noopener noreferrer" className="main-page__footer-link">
            About
          </a>
          <span className="main-page__footer-separator">·</span>
          {/* Test: FooterLinks: Source Code link visible in footer */}
          <a href="https://github.com/replayio/app-building/" target="_blank" rel="noopener noreferrer" className="main-page__footer-link">
            Source Code
          </a>
          <span className="main-page__footer-separator">·</span>
          {/* Test: FooterLinks: API Docs link visible in footer */}
          <a href="/api/v1/docs" target="_blank" rel="noopener noreferrer" className="main-page__footer-link" data-testid="api-docs-link">
            API Docs
          </a>
          <span className="main-page__footer-separator">·</span>
          <Link to="/status" className="main-page__footer-link" data-testid="status-link">
            System Status
          </Link>
        </nav>
      </footer>
    </div>
  )
}

export default MainPage
